import React, { Suspense } from "react";
import ProtectedRoute from "ProtectedRoute";
import { Navigate } from "react-router-dom";

// Material icons
import DashboardIcon from "@mui/icons-material/Dashboard";
import PropaneTankIcon from "@mui/icons-material/PropaneTank";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DeleteIcon from "@mui/icons-material/Delete";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BuildIcon from "@mui/icons-material/Build";
import SecurityIcon from "@mui/icons-material/Security";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AssessmentIcon from "@mui/icons-material/Assessment";
import InboxIcon from "@mui/icons-material/Inbox";

// Lazy-loaded AFMS pages
const TankDashboard = React.lazy(() => import("afms/pages/Tanks/TankDashboard"));
const TankListing = React.lazy(() => import("afms/pages/Tanks/TankListing"));
const TankDetail = React.lazy(() => import("afms/pages/Tanks/TankDetail"));
const EventHistory = React.lazy(() => import("afms/pages/Inventory/EventHistory"));
const LeakDetection = React.lazy(() => import("afms/pages/Inventory/LeakDetection"));
const OilInventory = React.lazy(() => import("afms/pages/Inventory/OilInventory"));
const PickupRequests = React.lazy(() => import("afms/pages/Pickups/PickupRequests"));
const PickupCalendar = React.lazy(() => import("afms/pages/Pickups/PickupCalendar"));
const AFMSVendorManagement = React.lazy(() => import("afms/pages/Pickups/VendorManagement"));
const BinDashboard = React.lazy(() => import("afms/pages/Bins/BinDashboard"));
const BinListing = React.lazy(() => import("afms/pages/Bins/BinListing"));
const BinDetail = React.lazy(() => import("afms/pages/Bins/BinDetail"));
const BinForm = React.lazy(() => import("afms/pages/Bins/BinForm"));
const InspectionList = React.lazy(() => import("afms/pages/Inspections/InspectionList"));
const InspectionScheduleForm = React.lazy(() => import("afms/pages/Inspections/InspectionScheduleForm"));
const InspectionComplianceDashboard = React.lazy(() => import("afms/pages/Inspections/ComplianceDashboard"));
const ChecklistManagement = React.lazy(() => import("afms/pages/Inspections/ChecklistManagement"));
const MaintenanceList = React.lazy(() => import("afms/pages/Maintenance/MaintenanceList"));
const MaintenanceCalendar = React.lazy(() => import("afms/pages/Maintenance/MaintenanceCalendar"));
const MaintenanceTaskForm = React.lazy(() => import("afms/pages/Maintenance/MaintenanceTaskForm"));
const MaintenanceComplianceDashboard = React.lazy(() => import("afms/pages/Maintenance/ComplianceDashboard"));
const VendorDashboard = React.lazy(() => import("afms/pages/Vendor/VendorDashboard"));
const CommandCenter = React.lazy(() => import("afms/pages/CommandCenter"));
const EnvironmentalCommandCenter = React.lazy(() => import("afms/pages/EnvironmentalCommandCenter"));
const EnvironmentalCommandCenterHelp = React.lazy(() => import("afms/pages/Help"));
const CommandCenterHelp = React.lazy(() => import("afms/pages/Help/CommandCenterHelp"));
const SelectedTicketDrawerHarness = React.lazy(() => import("afms/pages/Dev/SelectedTicketDrawerHarness"));
const TicketWorkflowSurface = React.lazy(() => import("afms/pages/WorkflowRoutes/TicketWorkflowSurface"));
const TicketDetail = React.lazy(() => import("afms/pages/Tickets/TicketDetail"));
const WorkflowSetup = React.lazy(() => import("afms/pages/Setup/WorkflowSetup"));
const ManualVendorConsole = React.lazy(() => import("afms/pages/ManualVendor/ManualVendorConsole"));
const ProofPackageConsole = React.lazy(() => import("afms/pages/Proof/ProofPackageConsole"));
const FinancialOutcomeConsole = React.lazy(() => import("afms/pages/Financial/FinancialOutcomeConsole"));
const ApHandoffConsole = React.lazy(() => import("afms/pages/AP/ApHandoffConsole"));
const RedistributionList = React.lazy(() => import("afms/pages/Redistribution/RedistributionList"));
const RedistributionDetail = React.lazy(() => import("afms/pages/Redistribution/RedistributionDetail"));
const InquiryResponse = React.lazy(() => import("afms/pages/Redistribution/InquiryResponse"));
const SourceVerification = React.lazy(() => import("afms/pages/Redistribution/SourceVerification"));

// Tank Notification Rules & Settings
const NotificationRules = React.lazy(() => import("afms/pages/Tanks/NotificationRules"));
const HeatingSeasonConfig = React.lazy(() => import("afms/pages/Tanks/HeatingSeasonConfig"));
const NotificationRuleLogs = React.lazy(() => import("afms/pages/Tanks/NotificationRuleLogs"));

// SPCC Compliance
const SpccDashboard = React.lazy(() => import("afms/pages/Spcc/SpccDashboard"));
const ContainmentAreaList = React.lazy(() => import("afms/pages/Spcc/ContainmentAreas/ContainmentAreaList"));
const ContainmentAreaForm = React.lazy(() => import("afms/pages/Spcc/ContainmentAreas/ContainmentAreaForm"));
const ContainmentAreaDetail = React.lazy(() => import("afms/pages/Spcc/ContainmentAreas/ContainmentAreaDetail"));
const PlanList = React.lazy(() => import("afms/pages/Spcc/Plans/PlanList"));
const PlanDetail = React.lazy(() => import("afms/pages/Spcc/Plans/PlanDetail"));
const GeneratePlanForm = React.lazy(() => import("afms/pages/Spcc/Plans/GeneratePlanForm"));
const StateOverlayList = React.lazy(() => import("afms/pages/Spcc/StateOverlays/StateOverlayList"));
const StateOverlayForm = React.lazy(() => import("afms/pages/Spcc/StateOverlays/StateOverlayForm"));
const SpccAuditTrail = React.lazy(() => import("afms/pages/Spcc/AuditTrail"));
const FacilityProfile = React.lazy(() => import("afms/pages/Spcc/FacilityProfile"));
const PlanReviews = React.lazy(() => import("afms/pages/Spcc/PlanReviews"));
const DikeDrainageLog = React.lazy(() => import("afms/pages/Spcc/DikeDrainageLog"));
const DischargeNotifications = React.lazy(() => import("afms/pages/Spcc/DischargeNotifications"));
const TierIPlanDocument = React.lazy(() => import("afms/pages/Spcc/Plans/TierIPlanDocument"));
const ScreeningCalculator = React.lazy(() => import("afms/pages/Spcc/ScreeningCalculator"));
const AFMSWizard = React.lazy(() => import("pages/AFMSWizard"));

// Financial & AP detail pages
const InvoiceUpload = React.lazy(() => import("afms/pages/Financial/InvoiceUpload"));
const CreditMemoUpload = React.lazy(() => import("afms/pages/Financial/CreditMemoUpload"));
const APCorrection = React.lazy(() => import("afms/pages/AP/APCorrection"));

// Phase 4: Vendor-Facing Pages
const VendorWorkOrderList = React.lazy(() => import("afms/pages/Vendor/VendorWorkOrderList"));
const VendorWorkOrderDetail = React.lazy(() => import("afms/pages/Vendor/VendorWorkOrderDetail"));
const VendorScheduleUpdate = React.lazy(() => import("afms/pages/Vendor/VendorScheduleUpdate"));
const VendorProofUpload = React.lazy(() => import("afms/pages/Vendor/VendorProofUpload"));
const VendorInvoiceCreditSubmit = React.lazy(() => import("afms/pages/Vendor/VendorInvoiceCreditSubmit"));
const VendorClarificationResponse = React.lazy(() => import("afms/pages/Vendor/VendorClarificationResponse"));
const VendorCloseoutStatus = React.lazy(() => import("afms/pages/Vendor/VendorCloseoutStatus"));
const VendorNotifications = React.lazy(() => import("afms/pages/Vendor/VendorNotifications"));

// Phase 4: Asset Setup Wizard (17-step)
const AssetSetupWizard = React.lazy(() => import("afms/pages/Setup/AssetSetupWizard"));

// Phase 4: Setup/Config Rule CRUD Pages
const MaterialStreamSetup = React.lazy(() => import("afms/pages/Setup/MaterialStreamSetup"));
const FinancialRuleSetup = React.lazy(() => import("afms/pages/Setup/FinancialRuleSetup"));
const ProofRuleSetup = React.lazy(() => import("afms/pages/Setup/ProofRuleSetup"));
const ApprovalRoutingSetup = React.lazy(() => import("afms/pages/Setup/ApprovalRoutingSetup"));
const ApprovalGates = React.lazy(() => import("afms/pages/Setup/ApprovalGates"));
const ServiceAgreements = React.lazy(() => import("afms/pages/Setup/ServiceAgreements"));
const Onboarding = React.lazy(() => import("afms/pages/Setup/Onboarding"));
const OnboardingRelationship = React.lazy(() => import("afms/pages/Setup/Onboarding/Relationship"));
const PickupWorkflow = React.lazy(() => import("afms/pages/Settings/PickupWorkflow"));
const PickupSetupTestConfirm = React.lazy(() => import("afms/pages/Settings/PickupWorkflow/SetupTestConfirm"));
const WorkflowNotifications = React.lazy(() => import("afms/pages/Setup/WorkflowNotifications"));
const Inbox = React.lazy(() => import("afms/pages/Inbox"));
const ApprovalNotifications = React.lazy(() => import("afms/pages/Setup/ApprovalNotifications"));
const SLAPolicySetup = React.lazy(() => import("afms/pages/Setup/SLAPolicySetup"));
const NotificationRuleSetup = React.lazy(() => import("afms/pages/Setup/NotificationRuleSetup"));
const APCloseoutRuleSetup = React.lazy(() => import("afms/pages/Setup/APCloseoutRuleSetup"));
const VendorWorkflowProfileSetup = React.lazy(() => import("afms/pages/Setup/VendorWorkflowProfileSetup"));
const VendorContactSetup = React.lazy(() => import("afms/pages/Setup/VendorContactSetup"));

// Phase 5: Monitoring, Reporting & Admin Dashboards
const ConfigCompleteness = React.lazy(() => import("afms/pages/Reports/ConfigCompleteness"));
const ManualExceptions = React.lazy(() => import("afms/pages/Reports/ManualExceptions"));
const APQueue = React.lazy(() => import("afms/pages/AP/APQueue"));
const AuditExport = React.lazy(() => import("afms/pages/Audit/AuditExport"));
const WasteReports = React.lazy(() => import("afms/pages/Reports/WasteReports"));
const APStatus = React.lazy(() => import("afms/pages/AP/APStatus"));
const TelemetryHealth = React.lazy(() => import("afms/pages/Reports/TelemetryHealth"));
const SLARiskQueue = React.lazy(() => import("afms/pages/Reports/SLARiskQueue"));
const CapacityRiskQueue = React.lazy(() => import("afms/pages/Reports/CapacityRiskQueue"));
const VendorPerformance = React.lazy(() => import("afms/pages/Reports/VendorPerformance"));
const AuditGapQueue = React.lazy(() => import("afms/pages/Reports/AuditGapQueue"));
const WorkflowTrainingHub = React.lazy(() => import("afms/pages/Training/WorkflowTrainingHub"));

// Phase 6: Compliance, Audit & Simulation
const TrainingRecordEntry = React.lazy(() => import("afms/pages/Training/TrainingRecordEntry"));
const RetrainingRequest = React.lazy(() => import("afms/pages/Training/RetrainingRequest"));
const ProofPackageViewer = React.lazy(() => import("afms/pages/Compliance/ProofPackageViewer"));
const AssetDocumentLibrary = React.lazy(() => import("afms/pages/Compliance/AssetDocumentLibrary"));
const CorrectiveActionEntry = React.lazy(() => import("afms/pages/Compliance/CorrectiveActionEntry"));
const IntegritySchedule = React.lazy(() => import("afms/pages/Spcc/IntegritySchedule"));
const WorkflowSimulation = React.lazy(() => import("afms/pages/Admin/WorkflowSimulation"));
const VersionComparison = React.lazy(() => import("afms/pages/Admin/Workflows/VersionComparison"));
const WorkflowDiagramViewer = React.lazy(() => import("afms/pages/Admin/WorkflowDiagramViewer"));

// Phase 3+ modules
const Labels = React.lazy(() => import("afms/pages/Labels"));
const TankControl = React.lazy(() => import("afms/pages/TankControl"));
const AdvancedInspection = React.lazy(() => import("afms/pages/AdvancedInspection"));
const Invoices = React.lazy(() => import("afms/pages/Invoices"));
const Findings = React.lazy(() => import("afms/pages/Findings"));
const VendorOps = React.lazy(() => import("afms/pages/VendorOps"));

// Blueprint v2: Dispatch & Closeout
const DispatchConsole = React.lazy(() => import("afms/pages/Dispatch/DispatchConsole"));
const TruckManagement = React.lazy(() => import("afms/pages/Dispatch/TruckManagement"));
const DriverManagement = React.lazy(() => import("afms/pages/Dispatch/DriverManagement"));
const RouteBuilder = React.lazy(() => import("afms/pages/Dispatch/RouteBuilder"));
const PickupCloseout = React.lazy(() => import("afms/pages/Pickups/PickupCloseout"));
const PickupDetail = React.lazy(() => import("afms/pages/Pickups/PickupDetail"));
const PickupAccounting = React.lazy(() => import("afms/pages/Pickups/PickupAccounting"));

// Work Order Detail
const WorkOrderDetail = React.lazy(() => import("afms/pages/WorkOrders/WorkOrderDetail"));

// Workflow Blueprint § 13 admin surface.
const WorkflowsList = React.lazy(() => import("afms/pages/Admin/Workflows"));
const WorkflowDetail = React.lazy(() => import("afms/pages/Admin/Workflows/WorkflowDetail"));
const WorkflowVersionDetail = React.lazy(() => import("afms/pages/Admin/Workflows/VersionDetail"));
const PickupReporting = React.lazy(() => import("afms/pages/Pickups/PickupReporting"));
const OilAudit = React.lazy(() => import("afms/pages/Inventory/OilAudit"));

// Blueprint v2: Safety & Compliance
const IncidentList = React.lazy(() => import("afms/pages/Safety/IncidentList"));
const IncidentDetail = React.lazy(() => import("afms/pages/Safety/IncidentDetail"));
const IncidentForm = React.lazy(() => import("afms/pages/Safety/IncidentForm"));
const LotoList = React.lazy(() => import("afms/pages/Safety/LotoList"));
const LotoDetail = React.lazy(() => import("afms/pages/Safety/LotoDetail"));
const SafetyDashboard = React.lazy(() => import("afms/pages/Safety/SafetyDashboard"));

// Morning Briefing
const MorningBriefing = React.lazy(() => import("afms/pages/Briefing/MorningBriefing"));
const ArchiveBrowser = React.lazy(() => import("afms/pages/Briefing/ArchiveBrowser"));
const PilotMonitoring = React.lazy(() => import("afms/pages/Briefing/PilotMonitoring"));
const BriefingSettings = React.lazy(() => import("afms/pages/Briefing/BriefingSettings"));

// Blueprint v2: Training & Readiness
const TrainingDashboard = React.lazy(() => import("afms/pages/Training/TrainingDashboard"));
const LearnerProfile = React.lazy(() => import("afms/pages/Training/LearnerProfile"));
const SiteReadinessPage = React.lazy(() => import("afms/pages/Training/SiteReadiness"));
const TrainingRules = React.lazy(() => import("afms/pages/Training/TrainingRules"));
const SyncOperations = React.lazy(() => import("afms/pages/Training/SyncOperations"));

// Approvals
const ApprovalInbox = React.lazy(() => import("afms/pages/Approvals/ApprovalInbox"));

// Accounting
const VendorAccounting = React.lazy(() => import("afms/pages/Accounting/VendorAccounting"));
const DealerAccounting = React.lazy(() => import("afms/pages/Accounting/DealerAccounting"));

// Reporting
const OilAuditReport = React.lazy(() => import("afms/pages/Reporting/OilAudit"));
const HeaterDraw = React.lazy(() => import("afms/pages/Reporting/HeaterDraw"));

// AFMS Dealer/Vendor Shared Asset Dashboard (Portal)
const PortalAssetDashboard = React.lazy(() => import("afms/pages/Portal/AssetDashboard"));
const PortalMorningBrief = React.lazy(() => import("afms/pages/Portal/MorningBrief"));
const PortalRouteReport = React.lazy(() => import("afms/pages/Portal/RouteReport"));
const PortalAdminSettings = React.lazy(() => import("afms/pages/Portal/AdminSettings"));
const PortalAdminUsers = React.lazy(() => import("afms/pages/Portal/AdminUsers"));

// Enterprise (Manage my Clients / Manage my Vendors) — shared component
// pair lives outside the afms tree because the same pages mount in
// /lcp/enterprise/* and /energy/enterprise/* too. Sidebar gating
// (requiresEnterpriseOwner) keeps these hidden unless the caller is the
// OWNER of an enterprise org.
const EnterpriseManageMyClients = React.lazy(() => import("pages/Enterprise/ManageMyClients"));
const EnterpriseManageMyVendors = React.lazy(() => import("pages/Enterprise/ManageMyVendors"));

const Lazy = ({ children }) => (
  <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>}>
    {children}
  </Suspense>
);

const afmsRoutes = [
  {
    key: "afms-canonical-envCommandCenter",
    route: "/afms/command-center/environmental",
    component: <ProtectedRoute component={<Lazy><TicketWorkflowSurface surface="environmental" /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-vendorCommandCenter",
    route: "/afms/command-center/vendor",
    component: <ProtectedRoute component={<Lazy><TicketWorkflowSurface surface="vendor" /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-ticketDetail",
    route: "/afms/tickets/:ticketId",
    component: <ProtectedRoute component={<Lazy><TicketDetail /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-workOrderByTicket",
    route: "/afms/work-orders/by-ticket/:ticketId",
    component: <ProtectedRoute component={<Lazy><TicketWorkflowSurface surface="workOrder" /></Lazy>} />,
    appMode: "afms",
  },
  // Work Order Detail — must come AFTER the more specific /by-ticket/:ticketId route
  {
    key: "afms-workOrderDetail",
    route: "/afms/work-orders/:id",
    component: <ProtectedRoute component={<Lazy><WorkOrderDetail /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-proofByTicket",
    route: "/afms/proof/by-ticket/:ticketId",
    component: <ProtectedRoute component={<Lazy><TicketWorkflowSurface surface="proof" /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-proofEvent",
    route: "/afms/proof/:eventId",
    component: <ProtectedRoute component={<Lazy><ProofPackageConsole /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-financialByTicket",
    route: "/afms/financial/by-ticket/:ticketId",
    component: <ProtectedRoute component={<Lazy><TicketWorkflowSurface surface="financial" /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-financialEvent",
    route: "/afms/financial/:eventId",
    component: <ProtectedRoute component={<Lazy><FinancialOutcomeConsole /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-apByTicket",
    route: "/afms/ap/by-ticket/:ticketId/handoff",
    component: <ProtectedRoute component={<Lazy><TicketWorkflowSurface surface="ap" /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-apEvent",
    route: "/afms/ap/:eventId/handoff",
    component: <ProtectedRoute component={<Lazy><ApHandoffConsole /></Lazy>} />,
    appMode: "afms",
  },
  // In-app user-guide pages (hidden — no sidebar entry; reachable by URL and
  // from the `?` Help drawers on the command-center pages).
  {
    key: "afms-help-ecc",
    route: "/afms/help/environmental-command-center",
    component: <ProtectedRoute component={<Lazy><EnvironmentalCommandCenterHelp /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-help-command-center",
    route: "/afms/help/command-center",
    component: <ProtectedRoute component={<Lazy><CommandCenterHelp /></Lazy>} />,
    appMode: "afms",
  },

  // Financial & AP event-scoped detail pages (hidden — no sidebar entry)
  {
    key: "afms-financial-invoice",
    route: "/afms/financial/:eventId/invoice",
    component: <ProtectedRoute component={<Lazy><InvoiceUpload /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-financial-credit",
    route: "/afms/financial/:eventId/credit",
    component: <ProtectedRoute component={<Lazy><CreditMemoUpload /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-ap-correction",
    route: "/afms/ap/:eventId/correction",
    component: <ProtectedRoute component={<Lazy><APCorrection /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-manualVendorByTicket",
    route: "/afms/manual-vendor/by-ticket/:ticketId",
    component: <ProtectedRoute component={<Lazy><TicketWorkflowSurface surface="manualVendor" /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-manualVendorEvent",
    route: "/afms/manual-vendor/:eventId",
    component: <ProtectedRoute component={<Lazy><ManualVendorConsole /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-trainingWorkflow",
    route: "/afms/workflow-training",
    component: <ProtectedRoute component={<Lazy><TicketWorkflowSurface surface="training" /></Lazy>} />,
    appMode: "afms",
  },
  {
    // P4: the EPA export (it used to render the SPCC dashboard).
    key: "afms-canonical-auditPack",
    route: "/afms/reports/audit-pack",
    component: <ProtectedRoute component={<Lazy><AuditExport /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-audit-export",
    route: "/afms/audit-export",
    component: <ProtectedRoute component={<Lazy><AuditExport /></Lazy>} />,
    appMode: "afms",
  },
  {
    // P5: waste-stream reports and SLA benchmarks.
    key: "afms-reports-wasteStreams",
    route: "/afms/reports/waste-streams",
    component: <ProtectedRoute component={<Lazy><WasteReports /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-spccAuditGaps",
    route: "/afms/spcc/audit-gaps",
    component: <ProtectedRoute component={<Lazy><TicketWorkflowSurface surface="compliance" /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-workflowSetup",
    route: "/afms/setup/workflows/new",
    component: <ProtectedRoute component={<Lazy><AssetSetupWizard /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-workflowSetupLegacy",
    route: "/afms/setup/workflows/flat",
    component: <ProtectedRoute component={<Lazy><WorkflowSetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-canonical-assetSettings",
    route: "/afms/settings/assets/:assetId",
    component: <ProtectedRoute component={<Lazy><AssetSetupWizard /></Lazy>} />,
    appMode: "afms",
  },
  // Vendor-facing pages (hidden — no sidebar, rendered under EnvironmentalPortalLayout)
  {
    key: "afms-vendor-workOrderList",
    route: "/afms/vendor/work-orders",
    component: <ProtectedRoute component={<Lazy><VendorWorkOrderList /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-vendor-workOrderDetail",
    route: "/afms/vendor/work-orders/:id",
    component: <ProtectedRoute component={<Lazy><VendorWorkOrderDetail /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-vendor-scheduleUpdate",
    route: "/afms/vendor/schedule/:workOrderId",
    component: <ProtectedRoute component={<Lazy><VendorScheduleUpdate /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-vendor-proofUpload",
    route: "/afms/vendor/proof/:eventId",
    component: <ProtectedRoute component={<Lazy><VendorProofUpload /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-vendor-financialSubmit",
    route: "/afms/vendor/financial/:eventId",
    component: <ProtectedRoute component={<Lazy><VendorInvoiceCreditSubmit /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-vendor-clarification",
    route: "/afms/vendor/clarification/:ticketId",
    component: <ProtectedRoute component={<Lazy><VendorClarificationResponse /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-vendor-closeoutStatus",
    route: "/afms/vendor/closeout/:ticketId",
    component: <ProtectedRoute component={<Lazy><VendorCloseoutStatus /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-vendor-notifications",
    route: "/afms/vendor/notifications",
    component: <ProtectedRoute component={<Lazy><VendorNotifications /></Lazy>} />,
    appMode: "afms",
  },
  // Setup/Config Rule CRUD pages (hidden — no sidebar)
  {
    key: "afms-settings-materials",
    route: "/afms/settings/materials",
    component: <ProtectedRoute component={<Lazy><MaterialStreamSetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-financialRules",
    route: "/afms/settings/financial-rules",
    component: <ProtectedRoute component={<Lazy><FinancialRuleSetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-financialRuleDetail",
    route: "/afms/settings/financial-rules/:id",
    component: <ProtectedRoute component={<Lazy><FinancialRuleSetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-proofRules",
    route: "/afms/settings/proof-rules",
    component: <ProtectedRoute component={<Lazy><ProofRuleSetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-proofRuleDetail",
    route: "/afms/settings/proof-rules/:id",
    component: <ProtectedRoute component={<Lazy><ProofRuleSetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-approvalRules",
    route: "/afms/settings/approval-rules",
    component: <ProtectedRoute component={<Lazy><ApprovalRoutingSetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-approvalRuleDetail",
    route: "/afms/settings/approval-rules/:id",
    component: <ProtectedRoute component={<Lazy><ApprovalRoutingSetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-approvalGates",
    route: "/afms/settings/approval-gates",
    component: <ProtectedRoute component={<Lazy><ApprovalGates /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-serviceAgreements",
    route: "/afms/settings/service-agreements",
    component: <ProtectedRoute component={<Lazy><ServiceAgreements /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-onboarding",
    route: "/afms/settings/onboarding",
    component: <ProtectedRoute component={<Lazy><Onboarding /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-onboardingRelationship",
    route: "/afms/settings/onboarding/:partnerId",
    component: <ProtectedRoute component={<Lazy><OnboardingRelationship /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-approvalNotifications",
    route: "/afms/settings/approval-notifications",
    component: <ProtectedRoute component={<Lazy><ApprovalNotifications /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-pickupWorkflow",
    route: "/afms/settings/pickup-workflow",
    component: <ProtectedRoute component={<Lazy><PickupWorkflow /></Lazy>} />,
    appMode: "afms",
  },
  {
    // Vendor side of the per-asset pickup setup test — the TEST notification links here.
    key: "afms-pickup-setup-test",
    route: "/afms/pickup-setup/tests/:publicId",
    component: <ProtectedRoute component={<Lazy><PickupSetupTestConfirm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-workflowNotifications",
    route: "/afms/settings/workflow-notifications",
    component: <ProtectedRoute component={<Lazy><WorkflowNotifications /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-inbox",
    route: "/afms/inbox",
    component: <ProtectedRoute component={<Lazy><Inbox /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-slaPolicies",
    route: "/afms/settings/sla-policies",
    component: <ProtectedRoute component={<Lazy><SLAPolicySetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-slaPolicyDetail",
    route: "/afms/settings/sla-policies/:id",
    component: <ProtectedRoute component={<Lazy><SLAPolicySetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-notificationRules",
    route: "/afms/settings/notification-rules",
    component: <ProtectedRoute component={<Lazy><NotificationRuleSetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-notificationRuleDetail",
    route: "/afms/settings/notification-rules/:id",
    component: <ProtectedRoute component={<Lazy><NotificationRuleSetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-apRules",
    route: "/afms/settings/ap-rules",
    component: <ProtectedRoute component={<Lazy><APCloseoutRuleSetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-apRuleDetail",
    route: "/afms/settings/ap-rules/:id",
    component: <ProtectedRoute component={<Lazy><APCloseoutRuleSetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-vendorWorkflowProfile",
    route: "/afms/settings/vendors/:vendorId/workflow-profile",
    component: <ProtectedRoute component={<Lazy><VendorWorkflowProfileSetup /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-settings-vendorContacts",
    route: "/afms/settings/vendors/:vendorId/contacts",
    component: <ProtectedRoute component={<Lazy><VendorContactSetup /></Lazy>} />,
    appMode: "afms",
  },
  // Phase 5: Monitoring, Reporting & Admin Dashboards (hidden — no sidebar)
  {
    key: "afms-reports-configCompleteness",
    route: "/afms/reports/missing-configuration",
    component: <ProtectedRoute component={<Lazy><ConfigCompleteness /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-reports-manualExceptions",
    route: "/afms/reports/manual-exceptions",
    component: <ProtectedRoute component={<Lazy><ManualExceptions /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-reports-apQueue",
    route: "/afms/reports/ap-queue",
    component: <ProtectedRoute component={<Lazy><APQueue /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-ap-queue",
    route: "/afms/ap-queue",
    component: <ProtectedRoute component={<Lazy><APQueue /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-reports-apStatus",
    route: "/afms/reports/ap-status",
    component: <ProtectedRoute component={<Lazy><APStatus /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-reports-telemetryHealth",
    route: "/afms/telemetry/health",
    component: <ProtectedRoute component={<Lazy><TelemetryHealth /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-reports-slaRisk",
    route: "/afms/reports/sla-risk",
    component: <ProtectedRoute component={<Lazy><SLARiskQueue /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-reports-capacityRisk",
    route: "/afms/reports/capacity-risk",
    component: <ProtectedRoute component={<Lazy><CapacityRiskQueue /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-reports-vendorPerformance",
    route: "/afms/reports/vendor-performance",
    component: <ProtectedRoute component={<Lazy><VendorPerformance /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-reports-auditGaps",
    route: "/afms/reports/audit-gaps",
    component: <ProtectedRoute component={<Lazy><AuditGapQueue /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-training-hub",
    route: "/afms/training/hub",
    component: <ProtectedRoute component={<Lazy><WorkflowTrainingHub /></Lazy>} />,
    appMode: "afms",
  },
  // Phase 6: Compliance, Audit & Simulation (hidden — no sidebar)
  {
    key: "afms-training-recordEntry",
    route: "/afms/training/record",
    component: <ProtectedRoute component={<Lazy><TrainingRecordEntry /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-training-retrain",
    route: "/afms/training/retrain",
    component: <ProtectedRoute component={<Lazy><RetrainingRequest /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-compliance-proofViewer",
    route: "/afms/compliance/proof/:ticketId",
    component: <ProtectedRoute component={<Lazy><ProofPackageViewer /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-compliance-documentLibrary",
    route: "/afms/compliance/documents/:assetId",
    component: <ProtectedRoute component={<Lazy><AssetDocumentLibrary /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-compliance-correctiveAction",
    route: "/afms/compliance/corrective-action",
    component: <ProtectedRoute component={<Lazy><CorrectiveActionEntry /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-spcc-integritySchedule",
    route: "/afms/spcc/integrity-schedule",
    component: <ProtectedRoute component={<Lazy><IntegritySchedule /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-admin-workflowSimulation",
    route: "/afms/admin/workflow-simulation",
    component: <ProtectedRoute component={<Lazy><WorkflowSimulation /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-admin-workflowCompare",
    route: "/afms/admin/workflows/compare",
    component: <ProtectedRoute component={<Lazy><VersionComparison /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-admin-workflowDiagram",
    route: "/afms/admin/workflow-diagram/:runId",
    component: <ProtectedRoute component={<Lazy><WorkflowDiagramViewer /></Lazy>} />,
    appMode: "afms",
  },

  // Dashboard
  {
    type: "collapse",
    name: "Dashboard",
    key: "afms-dashboard",
    icon: <DashboardIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Operations Overview",
        key: "afms-operationsOverview",
        route: "/afms/overview",
        // Jam cbef544f: retired — the Command Center is Environmental's home now.
        component: <Navigate to="/afms/environmental-command-center" replace />,
      },
      {
        name: "Tank Dashboard",
        key: "afms-tankDashboard",
        route: "/afms/dashboard",
        component: <ProtectedRoute component={<Lazy><TankDashboard /></Lazy>} />,
      },
      {
        name: "Command Center",
        key: "afms-commandCenter",
        route: "/afms/command-center",
        component: <ProtectedRoute component={<Lazy><CommandCenter /></Lazy>} />,
      },
      {
        name: "Environmental Operations",
        key: "afms-envCommandCenter",
        route: "/afms/environmental-command-center",
        component: <ProtectedRoute component={<Lazy><EnvironmentalCommandCenter /></Lazy>} />,
      },
      ...(process.env.NODE_ENV === "development" ? [{
        name: "Selected Ticket Drawer (dev)",
        key: "afms-dev-selectedTicketDrawer",
        route: "/afms/dev/selected-ticket-drawer",
        component: <ProtectedRoute component={<Lazy><SelectedTicketDrawerHarness /></Lazy>} />,
      }] : []),
      {
        name: "Vendor Dashboard",
        key: "afms-vendorDashboard",
        route: "/afms/vendor",
        component: <ProtectedRoute component={<Lazy><VendorDashboard /></Lazy>} />,
      },
      {
        name: "Morning Briefing",
        key: "afms-morningBriefing",
        route: "/afms/briefing",
        component: <ProtectedRoute component={<Lazy><MorningBriefing /></Lazy>} />,
      },
      {
        name: "Briefing Archive",
        key: "afms-briefingArchive",
        route: "/afms/briefing/archive",
        component: <ProtectedRoute component={<Lazy><ArchiveBrowser /></Lazy>} />,
      },
      {
        name: "Pilot Monitoring",
        key: "afms-pilotMonitoring",
        route: "/afms/briefing/pilot-monitoring",
        component: <ProtectedRoute component={<Lazy><PilotMonitoring /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },

  // Tank Monitoring Section
  { type: "title", title: "Recycling Monitoring", key: "afms-title-tanks", appMode: "afms" },
  {
    type: "collapse",
    name: "Tanks",
    key: "afms-tanks",
    icon: <PropaneTankIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "All Tanks",
        key: "afms-tankListing",
        route: "/afms/tanks",
        component: <ProtectedRoute component={<Lazy><TankListing /></Lazy>} />,
      },
      {
        name: "Oil Inventory",
        key: "afms-oilInventory",
        route: "/afms/inventory/oil",
        component: <ProtectedRoute component={<Lazy><OilInventory /></Lazy>} />,
      },
      {
        name: "Oil Audit",
        key: "afms-oilAudit",
        route: "/afms/inventory/oil-audit",
        component: <ProtectedRoute component={<Lazy><OilAudit /></Lazy>} />,
      },
      {
        name: "Event History",
        key: "afms-eventHistory",
        route: "/afms/inventory/events",
        component: <ProtectedRoute component={<Lazy><EventHistory /></Lazy>} />,
      },
      {
        name: "Leak Detection",
        key: "afms-leakDetection",
        route: "/afms/inventory/leaks",
        component: <ProtectedRoute component={<Lazy><LeakDetection /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },
  {
    key: "afms-tankAdd",
    route: "/afms/tanks/add",
    component: <ProtectedRoute component={<Lazy><AFMSWizard /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-tankDetail",
    route: "/afms/tanks/view",
    component: <ProtectedRoute component={<Lazy><TankDetail /></Lazy>} />,
    appMode: "afms",
  },
  // Inventory root route (redirects to Oil Inventory)
  {
    key: "afms-inventoryRoot",
    route: "/afms/inventory",
    component: <ProtectedRoute component={<Lazy><OilInventory /></Lazy>} />,
    appMode: "afms",
  },
  // Bins (moved under Recycling Monitoring, after Tanks)
  {
    type: "collapse",
    name: "Bins",
    key: "afms-bins",
    icon: <DeleteIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Bin Dashboard",
        key: "afms-binDashboard",
        route: "/afms/bins/dashboard",
        component: <ProtectedRoute component={<Lazy><BinDashboard /></Lazy>} />,
      },
      {
        name: "All Bins",
        key: "afms-binListing",
        route: "/afms/bins",
        component: <ProtectedRoute component={<Lazy><BinListing /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },
  {
    key: "afms-briefingRun",
    route: "/afms/briefing/:runId",
    component: <ProtectedRoute component={<Lazy><MorningBriefing /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-binDetail",
    route: "/afms/bins/:binId",
    component: <ProtectedRoute component={<Lazy><BinDetail /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-binAdd",
    route: "/afms/bins/add",
    component: <ProtectedRoute component={<Lazy><BinForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-binEdit",
    route: "/afms/bins/:binId/edit",
    component: <ProtectedRoute component={<Lazy><BinForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    type: "collapse",
    name: "Pickups",
    key: "afms-pickups",
    icon: <LocalShippingIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Pickup Calendar",
        key: "afms-pickupCalendar",
        route: "/afms/pickups/calendar",
        component: <ProtectedRoute component={<Lazy><PickupCalendar /></Lazy>} />,
      },
      {
        name: "Pickup Requests",
        key: "afms-pickupRequests",
        route: "/afms/pickups/requests",
        component: <ProtectedRoute component={<Lazy><PickupRequests /></Lazy>} />,
      },
      {
        name: "Vendors",
        key: "afms-pickupVendors",
        route: "/afms/pickups/vendors",
        component: <ProtectedRoute component={<Lazy><AFMSVendorManagement /></Lazy>} />,
      },
      {
        name: "Accounting",
        key: "afms-pickupAccounting",
        route: "/afms/pickups/accounting",
        component: <ProtectedRoute component={<Lazy><PickupAccounting /></Lazy>} />,
      },
      {
        name: "Reporting",
        key: "afms-pickupReporting",
        route: "/afms/pickups/reporting",
        component: <ProtectedRoute component={<Lazy><PickupReporting /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },
  // Blueprint v2: Pickup Closeout (hidden route, accessed from pickup actions)
  {
    key: "afms-pickupCloseout",
    route: "/afms/pickups/:pickupId/closeout",
    component: <ProtectedRoute component={<Lazy><PickupCloseout /></Lazy>} />,
    appMode: "afms",
  },
  // Pickup Detail — must be declared AFTER specific /afms/pickups/* routes so
  // those static segments match before the :pickupId wildcard is evaluated.
  {
    key: "afms-pickupDetail",
    route: "/afms/pickups/:pickupId",
    component: <ProtectedRoute component={<Lazy><PickupDetail /></Lazy>} />,
    appMode: "afms",
  },

  // Dispatch (under Tank Monitoring, after Pickups)
  {
    type: "collapse",
    name: "Dispatch",
    key: "afms-dispatch",
    icon: <LocalShippingIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Console",
        key: "afms-dispatchConsole",
        route: "/afms/dispatch",
        component: <ProtectedRoute component={<Lazy><DispatchConsole /></Lazy>} />,
      },
      {
        name: "Trucks",
        key: "afms-trucks",
        route: "/afms/dispatch/trucks",
        component: <ProtectedRoute component={<Lazy><TruckManagement /></Lazy>} />,
      },
      {
        name: "Drivers",
        key: "afms-drivers",
        route: "/afms/dispatch/drivers",
        component: <ProtectedRoute component={<Lazy><DriverManagement /></Lazy>} />,
      },
      {
        name: "Routes",
        key: "afms-routes",
        route: "/afms/dispatch/routes",
        component: <ProtectedRoute component={<Lazy><RouteBuilder /></Lazy>} />,
      },
      {
        name: "Redistribution",
        key: "afms-redistributionListing",
        route: "/afms/dispatch/redistribution",
        component: <ProtectedRoute component={<Lazy><RedistributionList /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },
  {
    key: "afms-routeDetail",
    route: "/afms/dispatch/routes/:routeId",
    component: <ProtectedRoute component={<Lazy><RouteBuilder /></Lazy>} />,
    appMode: "afms",
  },

  // Blueprint v2: Safety routes (sidebar item moved under Compliance)
  {
    key: "afms-safetyRoot",
    route: "/afms/safety",
    component: <ProtectedRoute component={<Lazy><SafetyDashboard /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-incidentDetail",
    route: "/afms/safety/incidents/:incidentId",
    component: <ProtectedRoute component={<Lazy><IncidentDetail /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-incidentReport",
    route: "/afms/safety/incidents/report",
    component: <ProtectedRoute component={<Lazy><IncidentForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-lotoDetail",
    route: "/afms/safety/loto/:lotoId",
    component: <ProtectedRoute component={<Lazy><LotoDetail /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-safetyDashboard",
    route: "/afms/safety/dashboard",
    component: <ProtectedRoute component={<Lazy><SafetyDashboard /></Lazy>} />,
    appMode: "afms",
  },

  // Training hidden routes (sidebar item moved under Compliance)
  {
    key: "afms-learnerProfile",
    route: "/afms/training/learner/:userId",
    component: <ProtectedRoute component={<Lazy><LearnerProfile /></Lazy>} />,
    appMode: "afms",
  },

  {
    key: "afms-redistributionDetail",
    route: "/afms/dispatch/redistribution/:caseId",
    component: <ProtectedRoute component={<Lazy><RedistributionDetail /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-inquiryResponse",
    route: "/afms/dispatch/redistribution/:caseId/respond",
    component: <ProtectedRoute component={<Lazy><InquiryResponse /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-sourceVerification",
    route: "/afms/dispatch/redistribution/:caseId/verify",
    component: <ProtectedRoute component={<Lazy><SourceVerification /></Lazy>} />,
    appMode: "afms",
  },

  // Tank Settings Section
  { type: "title", title: "Tank Settings", key: "afms-title-settings", appMode: "afms" },
  {
    type: "collapse",
    name: "Notification Rules",
    key: "afms-notificationRules",
    icon: <PropaneTankIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Rules",
        key: "afms-notificationRulesList",
        route: "/afms/settings/notification-rules",
        component: <ProtectedRoute component={<Lazy><NotificationRules /></Lazy>} />,
      },
      {
        name: "Heating Season",
        key: "afms-heatingSeasonConfig",
        route: "/afms/settings/heating-season",
        component: <ProtectedRoute component={<Lazy><HeatingSeasonConfig /></Lazy>} />,
      },
      {
        name: "Rule History",
        key: "afms-notificationRuleLogs",
        route: "/afms/settings/notification-logs",
        component: <ProtectedRoute component={<Lazy><NotificationRuleLogs /></Lazy>} />,
      },
      {
        name: "Briefing Settings",
        key: "afms-briefingSettings",
        route: "/afms/settings/briefing",
        component: <ProtectedRoute component={<Lazy><BriefingSettings /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER"],
    plans: [1, 2, 3],
    planTypes: [2],
  },

  // Compliance Section
  { type: "title", title: "Compliance", key: "afms-title-compliance", appMode: "afms" },
  // Safety
  {
    type: "collapse",
    name: "Safety",
    key: "afms-safety",
    icon: <SecurityIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Incidents",
        key: "afms-incidentList",
        route: "/afms/safety/incidents",
        component: <ProtectedRoute component={<Lazy><IncidentList /></Lazy>} />,
      },
      {
        name: "Lockout / Tagout",
        key: "afms-lotoList",
        route: "/afms/safety/loto",
        component: <ProtectedRoute component={<Lazy><LotoList /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },
  // SPCC Compliance
  {
    type: "collapse",
    name: "SPCC",
    key: "afms-spcc",
    icon: <SecurityIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Screening Calculator",
        key: "afms-spccScreening",
        route: "/afms/spcc/screening",
        component: <ProtectedRoute component={<Lazy><ScreeningCalculator /></Lazy>} />,
      },
      {
        name: "SPCC Dashboard",
        key: "afms-spccDashboard",
        route: "/afms/spcc/dashboard",
        component: <ProtectedRoute component={<Lazy><SpccDashboard /></Lazy>} />,
      },
      {
        name: "Containment Areas",
        key: "afms-spccContainmentAreas",
        route: "/afms/spcc/containment",
        component: <ProtectedRoute component={<Lazy><ContainmentAreaList /></Lazy>} />,
      },
      {
        name: "Plans",
        key: "afms-spccPlans",
        route: "/afms/spcc/plans",
        component: <ProtectedRoute component={<Lazy><PlanList /></Lazy>} />,
      },
      {
        name: "State Overlays",
        key: "afms-spccStateOverlays",
        route: "/afms/spcc/state-overlays",
        component: <ProtectedRoute component={<Lazy><StateOverlayList /></Lazy>} />,
      },
      {
        name: "Plan Reviews",
        key: "afms-spccPlanReviews",
        route: "/afms/spcc/plan-reviews",
        component: <ProtectedRoute component={<Lazy><PlanReviews /></Lazy>} />,
      },
      {
        name: "Dike Drainage",
        key: "afms-spccDikeDrainage",
        route: "/afms/spcc/dike-drainage",
        component: <ProtectedRoute component={<Lazy><DikeDrainageLog /></Lazy>} />,
      },
      {
        name: "Discharge Log",
        key: "afms-spccDischargeNotifications",
        route: "/afms/spcc/discharge-notifications",
        component: <ProtectedRoute component={<Lazy><DischargeNotifications /></Lazy>} />,
      },
      {
        name: "Audit Trail",
        key: "afms-spccAudit",
        route: "/afms/spcc/audit",
        component: <ProtectedRoute component={<Lazy><SpccAuditTrail /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },
  // Training (moved under Compliance, after SPCC)
  {
    type: "collapse",
    name: "Training",
    key: "afms-training",
    icon: <AssignmentIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Dashboard",
        key: "afms-trainingDashboard",
        route: "/afms/training",
        component: <ProtectedRoute component={<Lazy><TrainingDashboard /></Lazy>} />,
      },
      {
        name: "Site Readiness",
        key: "afms-siteReadiness",
        route: "/afms/training/site-readiness",
        component: <ProtectedRoute component={<Lazy><SiteReadinessPage /></Lazy>} />,
      },
      {
        name: "Assignment Rules",
        key: "afms-trainingRules",
        route: "/afms/training/rules",
        component: <ProtectedRoute component={<Lazy><TrainingRules /></Lazy>} />,
      },
      {
        name: "Sync Operations",
        key: "afms-syncOps",
        route: "/afms/training/sync",
        component: <ProtectedRoute component={<Lazy><SyncOperations /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },
  {
    type: "collapse",
    name: "Inspections",
    key: "afms-inspections",
    icon: <AssignmentIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Compliance Dashboard",
        key: "afms-inspectionDashboard",
        route: "/afms/inspections/dashboard",
        component: <ProtectedRoute component={<Lazy><InspectionComplianceDashboard /></Lazy>} />,
      },
      {
        name: "All Inspections",
        key: "afms-inspectionListing",
        route: "/afms/inspections",
        component: <ProtectedRoute component={<Lazy><InspectionList /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },
  {
    key: "afms-inspectionScheduleAdd",
    route: "/afms/inspections/schedules/add",
    component: <ProtectedRoute component={<Lazy><InspectionScheduleForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-inspectionScheduleEdit",
    route: "/afms/inspections/schedules/:scheduleId/edit",
    component: <ProtectedRoute component={<Lazy><InspectionScheduleForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-inspectionScheduleDetail",
    route: "/afms/inspections/schedules/:scheduleId",
    component: <ProtectedRoute component={<Lazy><InspectionScheduleForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-inspectionChecklists",
    route: "/afms/inspections/checklists",
    component: <ProtectedRoute component={<Lazy><ChecklistManagement /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-inspectionRecordDetail",
    route: "/afms/inspections/records/:recordId",
    component: <ProtectedRoute component={<Lazy><InspectionList /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-inspectionRecordPerform",
    route: "/afms/inspections/records/:recordId/perform",
    component: <ProtectedRoute component={<Lazy><InspectionList /></Lazy>} />,
    appMode: "afms",
  },

  // Maintenance Section
  {
    type: "collapse",
    name: "Maintenance",
    key: "afms-maintenance",
    icon: <BuildIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Maintenance Dashboard",
        key: "afms-maintenanceDashboard",
        route: "/afms/maintenance/dashboard",
        component: <ProtectedRoute component={<Lazy><MaintenanceComplianceDashboard /></Lazy>} />,
      },
      {
        name: "All Maintenance",
        key: "afms-maintenanceListing",
        route: "/afms/maintenance",
        component: <ProtectedRoute component={<Lazy><MaintenanceList /></Lazy>} />,
      },
      {
        name: "Calendar",
        key: "afms-maintenanceCalendar",
        route: "/afms/maintenance/calendar",
        component: <ProtectedRoute component={<Lazy><MaintenanceCalendar /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },
  {
    key: "afms-maintenanceScheduleAdd",
    route: "/afms/maintenance/schedules/add",
    component: <ProtectedRoute component={<Lazy><MaintenanceTaskForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-maintenanceScheduleEdit",
    route: "/afms/maintenance/schedules/:scheduleId/edit",
    component: <ProtectedRoute component={<Lazy><MaintenanceTaskForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-maintenanceScheduleDetail",
    route: "/afms/maintenance/schedules/:scheduleId",
    component: <ProtectedRoute component={<Lazy><MaintenanceTaskForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-maintenanceTaskDetail",
    route: "/afms/maintenance/tasks/:taskId",
    component: <ProtectedRoute component={<Lazy><MaintenanceList /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-maintenanceTaskPerform",
    route: "/afms/maintenance/tasks/:taskId/perform",
    component: <ProtectedRoute component={<Lazy><MaintenanceList /></Lazy>} />,
    appMode: "afms",
  },

  // AFMS Ordering Wizard
  {
    key: "afms-orderingWizard",
    route: "/afms/ordering-wizard",
    component: <ProtectedRoute component={<Lazy><AFMSWizard /></Lazy>} />,
    appMode: "afms",
    type: "item",
    name: "Ordering Wizard",
    icon: <SecurityIcon />,
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },
  // SPCC hidden routes (static before parameterized)
  {
    key: "afms-spccContainmentAdd",
    route: "/afms/spcc/containment/add",
    component: <ProtectedRoute component={<Lazy><ContainmentAreaForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-spccContainmentEdit",
    route: "/afms/spcc/containment/:areaId/edit",
    component: <ProtectedRoute component={<Lazy><ContainmentAreaForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-spccContainmentDetail",
    route: "/afms/spcc/containment/:areaId",
    component: <ProtectedRoute component={<Lazy><ContainmentAreaDetail /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-spccPlanGenerate",
    route: "/afms/spcc/plans/generate",
    component: <ProtectedRoute component={<Lazy><GeneratePlanForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-spccPlanDetail",
    route: "/afms/spcc/plans/:planId",
    component: <ProtectedRoute component={<Lazy><PlanDetail /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-spccStateOverlayAdd",
    route: "/afms/spcc/state-overlays/add",
    component: <ProtectedRoute component={<Lazy><StateOverlayForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-spccStateOverlayEdit",
    route: "/afms/spcc/state-overlays/:overlayId/edit",
    component: <ProtectedRoute component={<Lazy><StateOverlayForm /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-spccFacilityProfile",
    route: "/afms/spcc/facility-profile/:campusId",
    component: <ProtectedRoute component={<Lazy><FacilityProfile /></Lazy>} />,
    appMode: "afms",
  },
  {
    key: "afms-spccTierIDocument",
    route: "/afms/spcc/plans/:planId/document",
    component: <ProtectedRoute component={<Lazy><TierIPlanDocument /></Lazy>} />,
    appMode: "afms",
  },

  // ---- Labels ----
  {
    key: "afms-labels",
    route: "/afms/labels",
    component: <ProtectedRoute component={<Lazy><Labels /></Lazy>} />,
    appMode: "afms",
    type: "item",
    name: "Labels",
    icon: <AssignmentIcon />,
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },

  // ---- Tank Control ----
  {
    key: "afms-tankControl",
    route: "/afms/tank-control/:tankMonitorId",
    component: <ProtectedRoute component={<Lazy><TankControl /></Lazy>} />,
    appMode: "afms",
  },

  // ---- Advanced Inspection ----
  {
    key: "afms-advancedInspection",
    route: "/afms/advanced-inspections",
    component: <ProtectedRoute component={<Lazy><AdvancedInspection /></Lazy>} />,
    appMode: "afms",
    type: "item",
    name: "Advanced Inspection",
    icon: <SecurityIcon />,
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee"],
    plans: [1, 2, 3],
    planTypes: [2],
  },

  // ---- Invoices ----
  {
    key: "afms-invoices",
    route: "/afms/invoices",
    component: <ProtectedRoute component={<Lazy><Invoices /></Lazy>} />,
    appMode: "afms",
    type: "item",
    name: "Invoices",
    icon: <AssignmentIcon />,
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee"],
    plans: [1, 2, 3],
    planTypes: [2],
  },

  // ---- Findings ----
  {
    key: "afms-findings",
    route: "/afms/findings",
    component: <ProtectedRoute component={<Lazy><Findings /></Lazy>} />,
    appMode: "afms",
  },

  // ---- Vendor Operations ----
  {
    key: "afms-vendorOps",
    route: "/afms/vendor-ops",
    component: <ProtectedRoute component={<Lazy><VendorOps /></Lazy>} />,
    appMode: "afms",
    type: "item",
    name: "Vendor Operations",
    icon: <LocalShippingIcon />,
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee"],
    plans: [1, 2, 3],
    planTypes: [2],
  },

  // ── Approvals Section ──────────────────────────────────────────────────────
  { type: "title", title: "Approvals & Accounting", key: "afms-title-approvals", appMode: "afms" },
  {
    type: "collapse",
    name: "Approvals",
    key: "afms-approvals",
    icon: <InboxIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Approval Inbox",
        key: "afms-approvalInbox",
        route: "/afms/approvals/inbox",
        component: <ProtectedRoute component={<Lazy><ApprovalInbox /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },

  // ── Accounting Section ─────────────────────────────────────────────────────
  {
    type: "collapse",
    name: "Accounting",
    key: "afms-accounting",
    icon: <AccountBalanceIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Vendor Accounting",
        key: "afms-vendorAccounting",
        route: "/afms/accounting/vendor",
        component: <ProtectedRoute component={<Lazy><VendorAccounting /></Lazy>} />,
      },
      {
        name: "Dealer Accounting",
        key: "afms-dealerAccounting",
        route: "/afms/accounting/dealer",
        component: <ProtectedRoute component={<Lazy><DealerAccounting /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },

  // ── Reporting Section ──────────────────────────────────────────────────────
  {
    type: "collapse",
    name: "Reporting",
    key: "afms-reporting",
    icon: <AssessmentIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Oil Audit",
        key: "afms-oilAuditReport",
        route: "/afms/reporting/oil-audit",
        component: <ProtectedRoute component={<Lazy><OilAuditReport /></Lazy>} />,
      },
      {
        name: "Heater Draw",
        key: "afms-heaterDraw",
        route: "/afms/reporting/heater-draw",
        component: <ProtectedRoute component={<Lazy><HeaterDraw /></Lazy>} />,
      },
    ],
    roles: ["RECYCLING", "Waste Oil", "OWNER", "Employee", "Partner"],
    plans: [1, 2, 3],
    planTypes: [2],
  },

  // AFMS Dealer/Vendor Shared Asset Dashboard (Portal)
  {
    type: "collapse",
    name: "AFMS Portal",
    key: "afms-portal",
    icon: <DashboardIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Asset Dashboard",
        key: "afms-portal-dashboard",
        route: "/afms/portal/dashboard",
        component: <ProtectedRoute component={<Lazy><PortalAssetDashboard /></Lazy>} />,
      },
      {
        name: "Morning Brief",
        key: "afms-portal-morning-brief",
        route: "/afms/portal/morning-brief",
        component: <ProtectedRoute component={<Lazy><PortalMorningBrief /></Lazy>} />,
      },
      {
        name: "Admin Settings",
        key: "afms-portal-admin-settings",
        route: "/afms/portal/admin/settings",
        component: <ProtectedRoute component={<Lazy><PortalAdminSettings /></Lazy>} />,
      },
      {
        name: "Admin Users",
        key: "afms-portal-admin-users",
        route: "/afms/portal/admin/users",
        component: <ProtectedRoute component={<Lazy><PortalAdminUsers /></Lazy>} />,
      },
    ],
  },
  {
    // Dynamic route only — not a sidebar entry.
    route: "/afms/portal/routes/:routeId",
    component: <ProtectedRoute component={<Lazy><PortalRouteReport /></Lazy>} />,
    appMode: "afms",
    type: "route",
    name: "Portal Route Report",
    key: "afms-portal-route-report",
    icon: <LocalShippingIcon />,
  },

  // Workflow Blueprint § 13 admin surface — list, detail, version view.
  {
    type: "collapse",
    name: "Workflow Library",
    key: "afms-admin-workflows",
    icon: <AssignmentIcon />,
    appMode: "afms",
    // Plan K Phase 2 — lane gate (the AFMS module IS the Environmental lane).
    laneKey: "lane.environmental",
    collapse: [
      {
        name: "Workflows",
        key: "afms-admin-workflows-list",
        route: "/afms/admin/workflows",
        component: <ProtectedRoute component={<Lazy><WorkflowsList /></Lazy>} />,
      },
    ],
  },
  {
    route: "/afms/admin/workflows/:id",
    component: <ProtectedRoute component={<Lazy><WorkflowDetail /></Lazy>} />,
    appMode: "afms",
    type: "route",
    name: "Workflow Detail",
    key: "afms-admin-workflow-detail",
    icon: <AssignmentIcon />,
  },
  {
    route: "/afms/admin/workflows/:id/versions/:versionId",
    component: <ProtectedRoute component={<Lazy><WorkflowVersionDetail /></Lazy>} />,
    appMode: "afms",
    type: "route",
    name: "Workflow Version",
    key: "afms-admin-workflow-version",
    icon: <AssignmentIcon />,
  },
  // Enterprise — hidden routes (sidebar gates visibility separately).
  {
    route: "/afms/enterprise/manage-clients",
    component: <ProtectedRoute component={<Lazy><EnterpriseManageMyClients /></Lazy>} />,
    appMode: "afms",
    type: "route",
    name: "Manage my Clients",
    key: "afms-enterprise-manage-clients",
  },
  {
    route: "/afms/enterprise/manage-vendors",
    component: <ProtectedRoute component={<Lazy><EnterpriseManageMyVendors /></Lazy>} />,
    appMode: "afms",
    type: "route",
    name: "Manage my Vendors",
    key: "afms-enterprise-manage-vendors",
  },
];

export default afmsRoutes;
